import { useCallback, useMemo } from 'react';
import _ from 'lodash';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import { filterEmptyFilters } from '../helpers/helpers';

export const useFilters = () => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const currentFilters = useMemo(() => {
    const properQuery = search.substring(1);
    return queryString.parse(properQuery);
  }, [search]);

  const getFiltersWithExceptions = useCallback(
    (exceptions: string[]) => {
      return Object.keys(currentFilters).reduce((res: any, key: any) => {
        const isKeyInExceptions = exceptions.includes(key);
        if (isKeyInExceptions) {
          return res;
        }
        return { ...res, [key]: currentFilters[key] };
      }, {});
    },
    [currentFilters]
  );

  const applyFilters = (filter: { [key: string]: any }) => {
    const newFiltersState = { ...currentFilters, ...filter };
    const filteredFilters = filterEmptyFilters(newFiltersState);
    const newFiltersQuery = queryString.stringify(filteredFilters);
    navigate({
      search: `?${newFiltersQuery}`,
    });
  };

  const applyManyNestedFilters = (
    filterArray: { id: string; filter: any }[],
    key?: string,
    additionalFilters?: any
  ) => {
    const nestedKey = key || 'members';
    const nestedFilters = currentFilters[nestedKey];
    const nestedObj = nestedFilters ? JSON.parse(nestedFilters as string) : {};
    const objReadyForStringify = filterArray.reduce(
      (resp: any, filterObj) => {
        const currentNestedFilters = nestedObj[filterObj.id] ? nestedObj[filterObj.id] : {};
        const newNestedFilters = { ...currentNestedFilters, ...filterObj.filter };
        const filteredFilters = filterEmptyFilters(newNestedFilters);
        return !_.isEmpty(filteredFilters)
          ? { ...resp, [filterObj.id]: filteredFilters }
          : _.omit(resp, filterObj.id);
      },
      { ...nestedObj }
    );
    const stringifiedObject = !_.isEmpty(objReadyForStringify)
      ? JSON.stringify(objReadyForStringify)
      : null;
    return applyFilters({ [nestedKey]: stringifiedObject, ...additionalFilters });
  };

  const applyNestedFilter = useCallback(
    (filterObj: any, id: string, key?: string) => {
      const nestedKey = key || 'members';
      const nestedFilters = currentFilters[nestedKey];
      const nestedObj = nestedFilters ? JSON.parse(nestedFilters as string) : {};
      const currentNestedFilters = nestedObj[id] ? nestedObj[id] : {};
      const newNestedFilters = { ...currentNestedFilters, ...filterObj };
      const filteredFilters = filterEmptyFilters(newNestedFilters);
      const objReadyForStringify = !_.isEmpty(filteredFilters)
        ? { ...nestedObj, [id]: filteredFilters }
        : _.omit(nestedObj, id);
      const stringifiedObject = !_.isEmpty(objReadyForStringify)
        ? JSON.stringify(objReadyForStringify)
        : null;
      return applyFilters({ [nestedKey]: stringifiedObject });
    },
    [currentFilters, applyFilters]
  );

  const getNestedFilters = (key: string) => {
    const nestedFilter: any = currentFilters[key];
    if (nestedFilter) {
      return JSON.parse(nestedFilter);
    }
    return {};
  };

  const manageTripleSortFilters = (value: any, key: string) => {
    switch (value) {
      case 'default': {
        const sortOrder = JSON.stringify({ [key]: 'asc' });
        return applyFilters({ sortOrder });
      }
      case 'asc': {
        const sortOrder = JSON.stringify({ [key]: 'desc' });
        return applyFilters({ sortOrder });
      }
      case 'desc': {
        const sortOrder = JSON.stringify({ [key]: 'default' });
        return applyFilters({ sortOrder });
      }
      default: {
        const sortOrder = JSON.stringify({ [key]: 'asc' });
        return applyFilters({ sortOrder });
      }
    }
  };

  const applyAndMergeFilters = (parentKey: string, key: string, value: any) => {
    const val = value || null;
    const thisFilter = currentFilters[parentKey];
    if (thisFilter) {
      // @ts-ignore
      const filters = JSON.parse(thisFilter);
      const filtersObj = { ...filters, ...{ [key]: val } };
      const filteredStatesVal = filterEmptyFilters(filtersObj);
      const filtersValue = JSON.stringify(filteredStatesVal);
      return applyFilters({ [parentKey]: filtersValue });
    }
    const filterValue = JSON.stringify({ [key]: val });
    applyFilters({ [parentKey]: filterValue });
  };

  return {
    currentFilters,
    getFiltersWithExceptions,
    getNestedFilters,
    applyFilters,
    applyNestedFilter,
    applyManyNestedFilters,
    manageTripleSortFilters,
    applyAndMergeFilters,
  };
};
